import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { GuestFormResponse } from './api-types';
import {
  FormErrorStage,
  reportFormOutcome,
  stateFromError
} from './formErrors';
import {
  cacheRecoveredSession,
  clearCachedSession,
  getCachedSession,
  setCachedSession
} from './guestFormSessionCache';
import {
  GuestFormSession,
  GuestFormSessionContext,
  GuestFormState
} from './GuestFormSessionContext';
import { useRedeemGuestForm } from './query';

/**
 * Reads the magic-link token from `?t=`, redeems it for an in-memory session, and exposes the
 * outcome to the runner.
 *
 * The token is never persisted. A page refresh re-runs this from the token still in the URL, and
 * that re-redeem IS the resume mechanism — the server hands back the saved answers — which is why
 * nothing needs to go into localStorage. It also means the `?t=` parameter must survive every
 * navigation this page performs.
 */
export const GuestFormSessionProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get('t');
  const { mutate: redeem, mutateAsync: redeemAsync } = useRedeemGuestForm();

  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [data, setData] = useState<GuestFormResponse | null>(null);
  const [outcome, setOutcome] = useState<GuestFormState | null>(null);
  const attemptedRef = useRef(false);
  // Single-flight guard: concurrent failed writes share one recovery.
  const recoveringRef = useRef<Promise<string | null> | null>(null);

  // Mirrors `sessionToken` so `getSessionToken()` can answer synchronously, before React has
  // re-rendered with a token a recovery just minted. Written only through `publishSessionToken`, so
  // the ref and the state cannot end up disagreeing about which token is live.
  const sessionTokenRef = useRef<string | null>(null);
  const publishSessionToken = useCallback((token: string | null) => {
    sessionTokenRef.current = token;
    setSessionToken(token);
  }, []);
  const getSessionToken = useCallback(
    (): string | null => sessionTokenRef.current,
    []
  );

  useEffect(() => {
    // Redeem once per mount. The ref guard stops React 18 StrictMode's double invoke from minting
    // two sessions — the second would invalidate the first server-side.
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    if (!linkToken) {
      setOutcome('missing-token');
      return;
    }

    // Taking on a payload is the same job whether it came from the wire or from the cache, and it
    // includes the part that is easy to leave out of one of them: an invitation can be redeemed
    // AFTER submitting, and the server says so by marking the response rather than failing — so the
    // success screen is reached by reading the payload, not by catching an error. Sharing this is
    // what stops the two paths drifting apart, which they had: only the wire path checked, so a
    // remount on a spent link handed the respondent an editable form back.
    const adopt = (response: GuestFormResponse) => {
      publishSessionToken(response.sessionToken);
      setData(response);
      if (response.submitted) {
        setOutcome('submitted');
      }
    };

    const cached = getCachedSession(linkToken);
    if (cached) {
      adopt(cached);
      return;
    }

    redeem(linkToken, {
      onSuccess: response => {
        setCachedSession(linkToken, response);
        adopt(response);
      },
      onError: error => {
        reportFormOutcome('redeem', error);
        setOutcome(stateFromError(error));
      }
    });
  }, [linkToken, redeem, publishSessionToken]);

  const markSubmitted = useCallback(() => {
    // The invitation is spent; a remount must not reactivate a consumed session.
    clearCachedSession();
    setOutcome('submitted');
  }, []);

  const markNeedsUpdate = useCallback(() => {
    setOutcome('needs-update');
  }, []);

  const recoverSession = useCallback((): Promise<string | null> => {
    if (recoveringRef.current) return recoveringRef.current;
    if (!linkToken) {
      setOutcome('missing-token');
      return Promise.resolve(null);
    }
    const attempt = (async (): Promise<string | null> => {
      try {
        const response = await redeemAsync(linkToken);
        // Refresh only the live token, keeping the original seed data: reseeding would reset the
        // form under someone who is mid-answer.
        cacheRecoveredSession(linkToken, response);
        publishSessionToken(response.sessionToken);
        return response.sessionToken;
      } catch (error) {
        reportFormOutcome('recovery', error);
        clearCachedSession();
        setOutcome(stateFromError(error));
        return null;
      } finally {
        recoveringRef.current = null;
      }
    })();
    recoveringRef.current = attempt;
    return attempt;
  }, [linkToken, redeemAsync, publishSessionToken]);

  const routeError = useCallback((error: unknown, stage: FormErrorStage) => {
    reportFormOutcome(stage, error);
    clearCachedSession();
    setOutcome(stateFromError(error));
  }, []);

  const value = useMemo<GuestFormSession>(() => {
    let state: GuestFormState = 'loading';
    if (outcome) {
      state = outcome;
    } else if (sessionToken) {
      state = 'active';
    }
    return {
      sessionToken,
      state,
      data,
      markSubmitted,
      markNeedsUpdate,
      getSessionToken,
      recoverSession,
      routeError
    };
  }, [
    sessionToken,
    data,
    outcome,
    markSubmitted,
    markNeedsUpdate,
    getSessionToken,
    recoverSession,
    routeError
  ]);

  return (
    <GuestFormSessionContext.Provider value={value}>
      {children}
    </GuestFormSessionContext.Provider>
  );
};
